import { z } from "zod/v4";

export const AlertSeverityList = [
  "success",
  "info",
  "warning",
  "error",
] as const;
export type AlertSeverity = (typeof AlertSeverityList)[number];

const ProcessSchemaList = [
  "putExperience",
  "putExperienceError",
  "userError",
  "downloadExperience",
  "startExperience",
  "requestCogs",
  "uploadCogsResult",
  "duplicateExperience",
  "syncPricingFunction",
  "uploadCogs",
  "fileBuild",
  "rolloutExperience",
] as const;

export type ProcessType = (typeof ProcessSchemaList)[number];

export type GetProcessIdOptions =
  | StartExperienceProcess
  | PutExperienceProcess
  | DownloadExperienceProcess
  | RequestCogsProcess
  | UploadCogsResultProcess
  | UploadCogsProcess
  | DuplicateExperienceProcess
  | FileBuildProcess
  | SyncPricingFunctionProcess
  | PutExperienceError
  | UserError
  | RolloutExperienceProcess;

type BaseProcess = {
  type: ProcessType;
};

interface PutExperienceProcess extends BaseProcess {
  type: "putExperience";
  experienceId: string;
}

interface PutExperienceError extends BaseProcess {
  type: "putExperienceError";
  experienceId: string;
}

interface UserError extends BaseProcess {
  type: "userError";
  orgId: string;
}

interface DownloadExperienceProcess extends BaseProcess {
  type: "downloadExperience";
  experienceId: string;
  datasetId: string;
}

interface StartExperienceProcess extends BaseProcess {
  type: "startExperience";
  experienceId: string;
}

interface RolloutExperienceProcess extends BaseProcess {
  type: "rolloutExperience";
  experienceId: string;
}

interface RequestCogsProcess extends BaseProcess {
  type: "requestCogs";
  orgId: string;
}

interface UploadCogsResultProcess extends BaseProcess {
  type: "uploadCogsResult";
  processId: string;
}

interface UploadCogsProcess extends BaseProcess {
  type: "uploadCogs";
  orgId: string;
}

interface DuplicateExperienceProcess extends BaseProcess {
  type: "duplicateExperience";
  orgId: string;
  originalExperienceId: string;
}

interface SyncPricingFunctionProcess extends BaseProcess {
  type: "syncPricingFunction";
  orgId: string;
}

interface FileBuildProcess extends BaseProcess {
  type: "fileBuild";
  orgId: string;
  experienceId?: string;
}

export const ProcessStatusTableDtoSchema = z.object({
  id: z.string(),
  orgId: z.string(),
  type: z.enum(ProcessSchemaList),
  severity: z.enum(AlertSeverityList),
  title: z.string(),
  isLoading: z.boolean().nullish(),
  loadingPercent: z.number().nullish(),
  message: z.string().nullish(),
  longMessage: z.string().nullish(),
  action: z
    .object({
      href: z.string().nullish(),
      acknowledge: z.boolean().nullish(),
      deleteOnOpen: z.boolean(),
    })
    .nullish(),
  actionMessage: z.string().nullish(),
  email: z.email().nullish(),
  startTime: z.number(),
});

export type ProcessStatusTableDto = z.infer<
  typeof ProcessStatusTableDtoSchema
> & {
  type: ProcessType;
};

export type OrgAlertType = Omit<ProcessStatusTableDto, "orgId">;
